import {
  AttributeFilter,
  AttributeFilterOption,
  CatalogueFilterQuery,
  CatalogueScopeFilters,
  CollectionDesignation,
  CollectionFilter,
  SearchAppQueryFlags,
  StylesSorting
} from "./interfaces";
import { SortingBy } from "../client/components/SortingDropdown";
import { AvailabilityFilter } from "../client/containers/AvailabilityFilterContainer";
import { CataloguePaginationData } from "../client/components/CataloguePagination";
import * as queryString from "querystring";
import { CatalogueState } from "../client/Catalogue";
import magentoToCatalogueAttributesMaps from "./magentoToCatalogueAttributeMaps.json";
import { broadcastUsedLegacyFilterEvent } from "../client/Analytics";
import querystring from "querystring";
import { hostnameToStorefront } from "@glamcorner/core-cf-lib/lib/client";

type CatalogueFilterStates = Pick<
  CatalogueState,
  "collectionFilter" | "availabilityFilter" | "paginationData" | "appliedAttributeFilters"
>;

export function createQueryFromFilters(
  params: CatalogueFilterStates & {
    catalogueScopeFilters: CatalogueScopeFilters;
  }
) {
  const {
    collectionFilter: { collectionDesignation, sorting },
    catalogueScopeFilters: { flags, categories, searchText },
    availabilityFilter: { availableNow, deliveryDates, postcode },
    paginationData: { offset, limit },
    appliedAttributeFilters
  } = params;

  const queryFlags: SearchAppQueryFlags[] = ["check_quantity", ...flags];

  switch (collectionDesignation) {
    case CollectionDesignation.OnDemand:
      queryFlags.push("on_demand");
      break;
    case CollectionDesignation.Subscription:
      queryFlags.push("subscription");
      break;
    default:
  }

  if (collectionDesignation === CollectionDesignation.Subscription && availableNow) {
    queryFlags.push("subscription_available_now");
  }

  if (collectionDesignation === CollectionDesignation.AvailableToBuy) {
    queryFlags.push("sale");
  }

  const { startDate, endDate } = deliveryDates;
  let dateRange = "";
  if (startDate && endDate) {
    dateRange = `${startDate}/${endDate}`;
  }

  let appliedFilters = {};
  appliedAttributeFilters.forEach(attributeFilter => {
    const options = attributeFilter.options.map(o => o.value);
    if (options.length > 0) {
      appliedFilters[attributeFilter.code] = [options];
    }
  });

  if (categories.length > 0) {
    appliedFilters["categories"] = categories;
  }

  const querySorting = StylesSorting[sorting];
  const catalogueSearchQuery: CatalogueFilterQuery = {
    date_range: dateRange,
    postcode,
    field:
      "designer,try_on_allowed,image_info,magento_product_id,name,record_type,discount_price_in_cents,retail_price_in_cents,price_in_cents,style_code,url",
    text: searchText,
    filters: {
      ...appliedFilters
    },
    paging: {
      offset,
      limit
    },
    sorting: [querySorting],
    flags: queryFlags,
    storefront: hostnameToStorefront()
  };

  return catalogueSearchQuery;
}

export function createURLHashFromPaginationData(paginationData: CataloguePaginationData): string {
  const paginationForUrl = {};
  Object.entries(paginationData).forEach(([key, value]) => {
    if (["total"].includes(key)) {
      return;
    }

    paginationForUrl[key] = value;
  });
  return querystring.stringify(paginationForUrl);
}

export function createURLHashFromFilters(
  collectionFilter: CollectionFilter,
  availabilityFilter: AvailabilityFilter,
  attributeFilters: AttributeFilter[]
): string {
  const filtersForURL = {};

  Object.entries(collectionFilter).forEach(([key, value]) => {
    filtersForURL[key] = value;
  });

  Object.entries(availabilityFilter).forEach(([key, value]) => {
    if (key === "deliveryDates") {
      filtersForURL["startDate"] = value.startDate;
      filtersForURL["endDate"] = value.endDate;
    } else {
      filtersForURL[key] = value;
    }
  });

  attributeFilters.forEach(filter => {
    let filterOptions: string[] = [];
    Object.entries(filter.options).forEach(([_, value]) => {
      filterOptions.push(value.label);
    });
    filtersForURL[filter.code] = filterOptions.join(",");
  });

  for (const key of Object.keys(filtersForURL)) {
    if (filtersForURL[key] === "") {
      delete filtersForURL[key];
    }
  }

  return querystring.stringify(filtersForURL);
}

function consumeQueryStringFilters(searchString: string) {
  const filterParamRegex = /[&?]filters=/;
  if (`${searchString}`.match(filterParamRegex)) {
    const params = queryString.parse(searchString.replace(/^\?/, ""));
    const filters = params["filters"];
    delete params["filters"];
    const queryStringWithoutFilters = `?${queryString.stringify(params)}`;
    window.history.replaceState({}, document.title, window.location.pathname + queryStringWithoutFilters);
    return typeof filters !== "string" ? filters.join("&") : filters;
  }
  return false;
}

function getFilterStringFromLocation(location: Location): string {
  const params = consumeQueryStringFilters(location.search);
  let hash = params || location.hash;

  // Handle case where query parameters were accidentally included after the url hash
  if (hash.includes("?")) {
    const queryParamStart = hash.indexOf("?");
    hash = hash.substr(queryParamStart);
  }

  if (hash.charAt(0) === "#") {
    hash = hash.substr(1);
  }

  return hash;
}

function parseFilterString(filterString: string): [{ [key: string]: string }, boolean] {
  let isMagentoLegacyFilterUrl = false;
  const parsedFilterMap = Object.assign({}, queryString.parse(filterString));
  const adjustedFilterMap = {};
  for (let [attributeName, attributeOptionString] of Object.entries(parsedFilterMap)) {
    // Map the magento attribute names to the new catalogue names
    if (magentoToCatalogueAttributesMaps.attributeNames[attributeName]) {
      isMagentoLegacyFilterUrl = true;
      attributeName = magentoToCatalogueAttributesMaps.attributeNames[attributeName];
    }

    adjustedFilterMap[decodeURIComponent(attributeName)] = decodeURIComponent(attributeOptionString as string);
  }
  return [adjustedFilterMap, isMagentoLegacyFilterUrl];
}

export function createFiltersFromLocation(
  location: Location,
  availableAttributeFilters: AttributeFilter[]
): CatalogueFilterStates {
  let isLabelLegacyFilterUrl = false;
  const filterString: string = getFilterStringFromLocation(location);
  const [parsedFilterMap, isMagentoLegacyFilterUrl] = parseFilterString(filterString);

  const collectionDesignation =
    (parsedFilterMap.collectionDesignation as CollectionDesignation) || CollectionDesignation.OnDemand;
  const sorting = (parsedFilterMap.sorting as SortingBy) || SortingBy.Newest;
  const availableNow = parsedFilterMap.availableNow === "true";
  const postcode = parsedFilterMap.postcode || "";
  const deliveryDates = {
    startDate: parsedFilterMap.startDate || "",
    endDate: parsedFilterMap.endDate || ""
  };
  const total = 1;
  const offset = Number(parsedFilterMap["offset"] || 0);
  const limit = Number(parsedFilterMap["limit"] || 24);

  const attributeFilters: AttributeFilter[] = [];
  availableAttributeFilters.forEach(({ code, label, options: availableOptions }: AttributeFilter) => {
    let parsedFilterOptionString: string = "";
    if (parsedFilterMap[code]) {
      parsedFilterOptionString = parsedFilterMap[code];
    } else if (parsedFilterMap[label]) {
      parsedFilterOptionString = parsedFilterMap[label];
      // we were using the attribute filter label is the past
      // so we want to keep track of how many urls are being used with legacy filters
      isLabelLegacyFilterUrl = true;
    }

    if (parsedFilterOptionString) {
      // Handle both the old magento attribute values and the new catalogue ones.
      // Magento -> "172/222/333", Catalogue -> "10,12,14"
      const optionSeparator = parsedFilterOptionString.includes("/") || code.includes("price") ? "/" : ",";
      const parsedOptions = parsedFilterOptionString.split(optionSeparator);
      const translatedOptions = () => parsedOptions.map(o => magentoToCatalogueAttributesMaps.attributeValues[o]);

      let options = availableOptions.filter(({ label: optionLabel, value: optionValue }: AttributeFilterOption) => {
        return (
          parsedOptions.includes(optionLabel) ||
          translatedOptions().includes(optionLabel) ||
          parsedOptions.includes(optionValue) // this last one is a corner case for legacy magento pricing
        );
      });

      if (code === "#style_price") {
        options = [
          {
            label: parsedFilterOptionString,
            value: parsedFilterOptionString.replace(/[$\s]/g, "").replace("-", ",")
          }
        ];
      }

      attributeFilters.push({
        code,
        label,
        options
      });
    }
  });

  if (isLabelLegacyFilterUrl || isMagentoLegacyFilterUrl) {
    broadcastUsedLegacyFilterEvent(filterString);
  }

  return {
    appliedAttributeFilters: attributeFilters,
    collectionFilter: {
      collectionDesignation,
      sorting
    },
    availabilityFilter: {
      availableNow,
      postcode,
      deliveryDates
    },
    paginationData: {
      limit,
      offset,
      total
    }
  };
}
